import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Grid,
    Typography,
    TextField,
    Paper,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from "@mui/material";
import { Helmet } from "react-helmet";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import Stack from "@mui/material/Stack";
import moment from "moment-timezone";
import dayjs from "dayjs";
import axios from "axios";
import db from "../firebase-config";
import { apiUrl } from "../constants";
import useCheckToken from "../hooks/useCheckToken";
import useExchangeToken from "../hooks/useExchangeToken";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

const shift_detail = [
    { value: "D", label: "D" },
    { value: "N", label: "N" },
];

const DEFECT_MAP = {
    coating: {
        area: "ฝั่งดิบ",
        defect_code: "Coating",
        remarkField: "rawRemark",
    },
    cooking: {
        area: "ฝั่งดิบ",
        defect_code: "Cooking",
        remarkField: "rawRemark",
    },
    cooling: {
        area: "ฝั่งสุก",
        defect_code: "Cooling",
        remarkField: "cookedRemark",
    },
    packing: {
        area: "ฝั่งสุก",
        defect_code: "Packing",
        remarkField: "cookedRemark",
    },
};

export default function Defect() {
    const { line } = useParams();
    const exchangeToken = useExchangeToken();
    const [isTokenExchanged, setIsTokenExchanged] = useState(false);
    const [error, setError] = useState(null);
    const today = new Date();
    const [date, setDate] = useState(moment(today).tz("Asia/Bangkok").format("YYYY-MM-DD"));
    const [selectedShift, setSelectedShift] = useState("D");
    const [skuList, setSkuList] = useState(null);
    const [defectRows, setDefectRows] = useState([]);
    const [saveErrors, setSaveErrors] = useState({});

    useCheckToken();

    const ac_token = useSelector((state) => state.auth.access_token);
    const display = useSelector((state) => state.auth.email);

    const saveTimersRef = useRef({});

    useEffect(() => {
        const fetchInitialTokenAndData = async () => {
            try {
                const token = await exchangeToken();
                if (token) {
                    setIsTokenExchanged(true);
                }
            } catch (err) {
                console.error("Error exchanging token:", err);
                setError("Failed to exchange token");
            }
        };

        fetchInitialTokenAndData();
    }, [exchangeToken]);

    const handleEditionShift = (event) => {
        setSelectedShift(event.target.value);
    };

    const getCellKey = (rowIndex, field) => `${rowIndex}-${field}`;

    const setCellError = (rowIndex, field, isError) => {
        const key = getCellKey(rowIndex, field);

        setSaveErrors((prev) => {
            if (!isError) {
                const next = { ...prev };
                delete next[key];
                return next;
            }

            return {
                ...prev,
                [key]: "ส่งไม่สำเร็จ",
            };
        });
    };

    const getExistingDefects = async ({ sku, area, defect_code }) => {
        try {
            const getUrl =
                `${apiUrl.GET_DEFECT_KORAT}` +
                `&line=${encodeURIComponent(line)}` +
                `&factory_date=${encodeURIComponent(date)}` +
                `&shift=${encodeURIComponent(selectedShift)}` +
                `&sku=${encodeURIComponent(sku)}` +
                `&defect_code=${encodeURIComponent(defect_code)}`;

            const response = await axios.get(getUrl, {
                headers: {
                    Authorization: `Bearer ${ac_token}`,
                },
            });

            const defectList = Array.isArray(response?.data)
                ? response.data
                : response?.data?.DEFECT ||
                  response?.data?.results ||
                  response?.data?.data ||
                  [];

            return defectList.filter((item) => {
                return (
                    String(item?.country) === "THAILAND" &&
                    String(item?.plant_id) === "4117" &&
                    String(item?.factory) === "00000000000000" &&
                    String(item?.line) === line &&
                    String(item?.factory_date).slice(0, 10) === date &&
                    String(item?.shift) === selectedShift &&
                    String(item?.sku) === String(sku) &&
                    String(item?.area) === String(area) &&
                    String(item?.defect_code) === String(defect_code)
                );
            });
        } catch (error) {
            console.log("GET defect error:", error);
            return [];
        }
    };

    const buildPayload = ({ row, field, value, isCreate = false }) => {
        const defectConfig = DEFECT_MAP[field];

        const currentDateTime = moment(new Date())
            .tz("Asia/Bangkok")
            .format("YYYY-MM-DDTHH:mm:ss");

        const payload = {
            country: "THAILAND",
            plant_id: "4117",
            factory: "00000000000000",
            line: line,
            factory_date: date,
            shift: selectedShift,
            sku: row.product,
            area: defectConfig.area,
            ng_count: Number(value) < 0 ? 0 : Number(value),
            defect_code: defectConfig.defect_code,
            defect_timestamp: currentDateTime,
            remark: row?.[defectConfig.remarkField] || "",
            updated_by: display,
            update_timestamp: currentDateTime,
        };

        if (isCreate) {
            payload.created_at = currentDateTime;
            payload.created_by = display;
        }

        return payload;
    };

    const deleteDefect = async ({ rowIndex, row, field }) => {
        const defectConfig = DEFECT_MAP[field];

        if (!defectConfig) return;
        if (!row?.product || row.product === "-") return;
        if (!selectedShift) return;

        const idField = `${field}_id`;

        try {
            const existingList = await getExistingDefects({
                sku: row.product,
                area: defectConfig.area,
                defect_code: defectConfig.defect_code,
            });

            if (existingList.length === 0) {
                setCellError(rowIndex, field, false);
                return;
            }

            await Promise.all(
                existingList.map(async (item) => {
                    const defectId = item?.id || item?.ID;
                    if (!defectId) return;
                    return axios.delete(
                        `${apiUrl.POST_DEFECT_KORAT}${defectId}/`,
                        {
                            headers: {
                                Authorization: `Bearer ${ac_token}`,
                            },
                        }
                    );
                })
            );

            setDefectRows((prev) =>
                prev.map((r, i) =>
                    i === rowIndex
                        ? {
                              ...r,
                              [field]: "",
                              [idField]: null,
                          }
                        : r
                )
            );

            setCellError(rowIndex, field, false);
        } catch (error) {
            console.log("delete defect error:", error);
            setCellError(rowIndex, field, true);
        }
    };

    const saveDefect = async ({ rowIndex, row, field, value }) => {
        const defectConfig = DEFECT_MAP[field];

        if (!defectConfig) return;
        if (!row?.product || row.product === "-") return;
        if (!selectedShift) return;

        if (value === "" || value === null || value === undefined) {
            await deleteDefect({ rowIndex, row, field });
            return;
        }

        if (Number.isNaN(Number(value))) {
            setCellError(rowIndex, field, true);
            return;
        }

        const idField = `${field}_id`;

        try {
            const existingList = await getExistingDefects({
                sku: row.product,
                area: defectConfig.area,
                defect_code: defectConfig.defect_code,
            });
            if (existingList.length > 0) {
                const patchPayload = buildPayload({
                    row,
                    field,
                    value,
                    isCreate: false,
                });

                await Promise.all(
                    existingList.map(async (item) => {
                        const defectId = item?.id || item?.ID;
                        if (!defectId) return;
                        return axios.patch(
                            `${apiUrl.POST_DEFECT_KORAT}${defectId}/`,
                            patchPayload,
                            {
                                headers: {
                                    Authorization: `Bearer ${ac_token}`,
                                },
                            }
                        );
                    })
                );

                setDefectRows((prev) =>
                    prev.map((r, i) =>
                        i === rowIndex
                            ? {
                                  ...r,
                                  [idField]: existingList.map(
                                      (item) => item?.id || item?.ID
                                  ),
                              }
                            : r
                    )
                );

                setCellError(rowIndex, field, false);
                return;
            }

            const postPayload = buildPayload({
                row,
                field,
                value,
                isCreate: true,
            });

            const response = await axios.post(
                apiUrl.POST_DEFECT_KORAT,
                postPayload,
                {
                    headers: {
                        Authorization: `Bearer ${ac_token}`,
                    },
                }
            );
            const createdId =
                response?.data?.id ||
                response?.data?.ID ||
                response?.data?.data?.id ||
                response?.data?.DEFECT?.id ||
                null;

            if (createdId) {
                setDefectRows((prev) =>
                    prev.map((r, i) =>
                        i === rowIndex
                            ? {
                                  ...r,
                                  [idField]: [createdId],
                              }
                            : r
                    )
                );
            }

            setCellError(rowIndex, field, false);
        } catch (error) {
            console.log("save defect error:", error);
            setCellError(rowIndex, field, true);
        }
    };

    const scheduleAutoSave = ({ rowIndex, row, field, value }) => {
        const key = `${rowIndex}-${field}`;

        if (saveTimersRef.current[key]) {
            clearTimeout(saveTimersRef.current[key]);
        }

        saveTimersRef.current[key] = setTimeout(() => {
            saveDefect({ rowIndex, row, field, value });
        }, 700);
    };

    const updateRemarkToExistingDefects = async ({
        rowIndex,
        areaType,
        nextRow,
    }) => {
        const fields =
            areaType === "raw"
                ? ["coating", "cooking"]
                : ["cooling", "packing"];

        fields.forEach((field) => {
            const value = nextRow[field];

            if (value !== "" && value !== null && value !== undefined) {
                scheduleAutoSave({
                    rowIndex,
                    row: nextRow,
                    field,
                    value,
                });
            }
        });
    };

    const handleDefectChange = (index, field, value) => {
        const newRows = [...defectRows];

        if (field === "rawRemark" || field === "cookedRemark") {
            newRows[index][field] = value;
            setDefectRows(newRows);

            updateRemarkToExistingDefects({
                rowIndex: index,
                areaType: field === "rawRemark" ? "raw" : "cooked",
                nextRow: newRows[index],
            });

            return;
        }

        const safeValue =
            value === "" || value === null || value === undefined
                ? ""
                : Number(value) < 0
                ? 0
                : value;

        newRows[index][field] = safeValue;
        setDefectRows(newRows);

        setCellError(index, field, false);

        scheduleAutoSave({
            rowIndex: index,
            row: newRows[index],
            field,
            value: safeValue,
        });
    };

    useEffect(() => {
        const oeeSubscribe = db
            .collection("prod_mes_ignition_oee")
            .onSnapshot((querySnapshot) => {
                const oeeDocs = []

                querySnapshot.forEach((doc) => {
                    const data = { ...doc.data(), id: doc.id };

                    if (doc.id.includes(date) && doc.id.includes(line)) {
                        oeeDocs.push(data);
                    }
                });

                const lastOee = oeeDocs[oeeDocs.length - 1];

                if (lastOee) {
                    setSkuList(lastOee);
                } else {
                    setSkuList(null);
                }
            });

        return () => {
            oeeSubscribe();
        };
    }, [date]);

    useEffect(() => {
        async function prepareRows() {
            const skuArray = skuList?.SKU_LIST || [];

            setSaveErrors({});

            if (skuArray.length === 0) {
                setDefectRows([]);
                return;
            }

            const rows = await Promise.all(
                skuArray.map(async (item, index) => {
                    const product = item?.sku || "-";

                    const baseRow = {
                        id: index + 1,
                        product,
                        target: item?.target || "-",
                        fg: item?.fg || "-",
                        rm: item?.rm || "-",
                        gw: item?.gw || "-",

                        coating: "",
                        cooking: "",
                        rawRemark: "",
                        cooling: "",
                        packing: "",
                        cookedRemark: "",

                        coating_id: null,
                        cooking_id: null,
                        cooling_id: null,
                        packing_id: null,
                    };

                    if (!product || product === "-") {
                        return baseRow;
                    }

                    const results = await Promise.all(
                        Object.entries(DEFECT_MAP).map(
                            async ([field, config]) => {
                                const existingList =
                                    await getExistingDefects({
                                        sku: product,
                                        area: config.area,
                                        defect_code: config.defect_code,
                                    });

                                return {
                                    field,
                                    config,
                                    existingList,
                                };
                            }
                        )
                    );

                    results.forEach(({ field, config, existingList }) => {
                        const latest =
                            existingList.length > 0
                                ? existingList.sort(
                                      (a, b) => Number(b.id) - Number(a.id)
                                  )[0]
                                : null;

                        if (!latest) return;

                        const ngCount =
                            latest?.ng_count ??
                            latest?.NG_COUNT ??
                            latest?.ngCount ??
                            "";

                        const remark =
                            latest?.remark ?? latest?.REMARK ?? "";

                        baseRow[field] = ngCount;

                        baseRow[`${field}_id`] = existingList.map(
                            (item) => item?.id || item?.ID
                        );

                        if (config.area === "ฝั่งดิบ" && remark) {
                            baseRow.rawRemark = remark;
                        }

                        if (config.area === "ฝั่งสุก" && remark) {
                            baseRow.cookedRemark = remark;
                        }
                    });

                    return baseRow;
                })
            );

            setDefectRows(rows);
        }

        prepareRows();
    }, [skuList, date, selectedShift]);

    useEffect(() => {
        return () => {
            Object.values(saveTimersRef.current).forEach((timer) => {
                clearTimeout(timer);
            });
        };
    }, []);

    return (
        <>
            <Helmet>
                <title>Defect | MES</title>
            </Helmet>

            <Box sx={{ width: "100%", minHeight: "100vh", p: 2 }}>
                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                        mb: 2,
                    }}
                >
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Typography sx={labelStyle}>Date</Typography>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <Stack spacing={1}>
                                    <DatePicker
                                        inputFormat="YYYY-MM-DD"
                                        value={dayjs(date)}
                                        onChange={(newValue) => {
                                            if (
                                                newValue &&
                                                newValue.isValid()
                                            ) {
                                                setDate(
                                                    newValue.format(
                                                        "YYYY-MM-DD"
                                                    )
                                                );
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                size="small"
                                                {...params}
                                                sx={dateFieldStyle}
                                            />
                                        )}
                                    />
                                </Stack>
                            </LocalizationProvider>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography sx={labelStyle}>Shift</Typography>

                            <TextField
                                select
                                fullWidth
                                size="small"
                                value={selectedShift || ""}
                                onChange={handleEditionShift}
                                sx={selectFieldStyle}
                            >
                                {shift_detail.map((item) => (
                                    <MenuItem
                                        key={item.value}
                                        value={item.value}
                                    >
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                    </Grid>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: "#0B2A55",
                            fontSize: 18,
                            mb: 2,
                        }}
                    >
                        Defect Details
                    </Typography>

                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            borderRadius: 2,
                            border: "1px solid #d9e2ec",
                            overflowX: "auto",
                        }}
                    >
                        <Table sx={{ minWidth: 1000 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        rowSpan={2}
                                        align="center"
                                        sx={headerCell}
                                    >
                                        #
                                    </TableCell>

                                    <TableCell
                                        rowSpan={2}
                                        align="center"
                                        sx={headerCell}
                                    >
                                        สินค้าตามแผน
                                    </TableCell>

                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{
                                            ...headerCell,
                                            backgroundColor: "#E3F2FD",
                                            color: "#0D47A1",
                                        }}
                                    >
                                        ฝั่งดิบ
                                    </TableCell>

                                    <TableCell
                                        colSpan={3}
                                        align="center"
                                        sx={{
                                            ...headerCell,
                                            backgroundColor: "#E8F5E9",
                                            color: "#1B5E20",
                                        }}
                                    >
                                        ฝั่งสุก
                                    </TableCell>
                                </TableRow>

                                <TableRow>
                                    <TableCell align="center" sx={headerCell}>
                                        Coating
                                    </TableCell>
                                    <TableCell align="center" sx={headerCell}>
                                        Cooking
                                    </TableCell>
                                    <TableCell align="center" sx={headerCell}>
                                        Remark
                                    </TableCell>
                                    <TableCell align="center" sx={headerCell}>
                                        Cooling
                                    </TableCell>
                                    <TableCell align="center" sx={headerCell}>
                                        Packing
                                    </TableCell>
                                    <TableCell align="center" sx={headerCell}>
                                        Remark
                                    </TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {defectRows.length > 0 ? (
                                    defectRows.map((item, index) => (
                                        <TableRow key={item.id}>
                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                {index + 1}
                                            </TableCell>

                                            <TableCell sx={bodyCell}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 700,
                                                        fontSize: 14,
                                                        color: "#0B2A55",
                                                    }}
                                                >
                                                    {item.product}
                                                </Typography>
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    type="number"
                                                    value={item.coating}
                                                    error={
                                                        !!saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "coating"
                                                            )
                                                        ]
                                                    }
                                                    helperText={
                                                        saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "coating"
                                                            )
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "coating",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    type="number"
                                                    value={item.cooking}
                                                    error={
                                                        !!saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "cooking"
                                                            )
                                                        ]
                                                    }
                                                    helperText={
                                                        saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "cooking"
                                                            )
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "cooking",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    value={item.rawRemark}
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "rawRemark",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    type="number"
                                                    value={item.cooling}
                                                    error={
                                                        !!saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "cooling"
                                                            )
                                                        ]
                                                    }
                                                    helperText={
                                                        saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "cooling"
                                                            )
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "cooling",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    type="number"
                                                    value={item.packing}
                                                    error={
                                                        !!saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "packing"
                                                            )
                                                        ]
                                                    }
                                                    helperText={
                                                        saveErrors[
                                                            getCellKey(
                                                                index,
                                                                "packing"
                                                            )
                                                        ] || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "packing",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>

                                            <TableCell
                                                align="center"
                                                sx={bodyCell}
                                            >
                                                <DefectInput
                                                    value={item.cookedRemark}
                                                    onChange={(e) =>
                                                        handleDefectChange(
                                                            index,
                                                            "cookedRemark",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell
                                            colSpan={8}
                                            align="center"
                                            sx={bodyCell}
                                        >
                                            ไม่พบ SKU ในวันที่เลือก
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
        </>
    );
}

const labelStyle = {
    fontWeight: 600,
    mb: 1,
    color: "#0B2A55",
    fontSize: 14,
};

const selectFieldStyle = {
    "& .MuiOutlinedInput-root": {
        borderRadius: 1.5,
        backgroundColor: "#fff",
        height: 48,
    },
};

const dateFieldStyle = {
    width: "100%",
    svg: {
        color: "#000000",
    },
    "& .MuiOutlinedInput-root": {
        borderRadius: 1.5,
        backgroundColor: "#fff",
        height: 48,
        "& fieldset": {
            borderColor: "#c4c2c2",
        },
        "&:hover fieldset": {
            borderColor: "#c4c2c2",
        },
        "&.Mui-focused fieldset": {
            borderColor: "#c4c2c2",
        },
    },
    "& .MuiOutlinedInput-input": {
        color: "#000000",
        fontWeight: "bold",
        fontSize: 16,
        height: "48px",
        padding: "0 14px",
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
    },
};

const headerCell = {
    fontSize: 15,
    fontWeight: "bold",
    border: "1px solid #d9e2ec",
    backgroundColor: "#008B8B",
    color: "#ffffff",
    height: 42,
    padding: "8px",
};

const bodyCell = {
    fontSize: 15,
    border: "1px solid #d9e2ec",
    padding: "6px 8px",
    height: 56,
};

function DefectInput({
    value,
    onChange,
    type = "text",
    error = false,
    helperText = "",
}) {
    return (
        <TextField
            type={type}
            size="small"
            value={value}
            onChange={onChange}
            error={error}
            helperText={helperText}
            FormHelperTextProps={{
                sx: {
                    m: 0,
                    mt: 0.3,
                    fontSize: 11,
                    color: "#d32f2f",
                    textAlign: "left",
                    lineHeight: 1.2,
                },
            }}
            sx={{
                width: "100%",
                "& .MuiOutlinedInput-root": {
                    height: 36,
                    borderRadius: 1.2,
                    backgroundColor: "#fff",
                    "& fieldset": {
                        borderColor: error ? "#d32f2f" : undefined,
                    },
                    "&:hover fieldset": {
                        borderColor: error ? "#d32f2f" : undefined,
                    },
                    "&.Mui-focused fieldset": {
                        borderColor: error ? "#d32f2f" : undefined,
                    },
                },
                "& .MuiOutlinedInput-input": {
                    fontSize: 14,
                    textAlign: type === "number" ? "center" : "left",
                    padding: "6px 10px",
                },
            }}
            inputProps={{
                min: type === "number" ? 0 : undefined,
            }}
        />
    );
}